// 올라타기 동작: 다가가기, 뛰어오르기, 내려오기, 털어내기, 머리 위 자리 맞추기. 프로토타입 1740–1819.
// 올라탈 수 있는지는 stackRules.ts의 순수 함수로 판정한다.
import * as THREE from 'three';
import type { NavGrid } from '../engine/nav';
import { moveTo } from './walker';
import { poseHooks } from './pose';
import { canClimb, stackOf } from './stackRules';
import type { Capy } from './agent';

/** 뛰어오르기·내려오기 한 번. toBase면 그 카피바라 머리 위로, 아니면 to 지점으로. */
export interface Hop { from: THREE.Vector3; to?: THREE.Vector3; toBase?: Capy | null; t0: number; dur: number; arc: number }

const vH = new THREE.Vector3();

/** base 머리 위 앉는 자리 (월드 좌표). */
function mountPoint(base: Capy, out: THREE.Vector3): THREE.Vector3 {
  base.c.root.updateMatrixWorld(true);
  return base.c.headG.localToWorld(out.set(0, 0.48, -0.04));
}

/** 탑 옆에 내려설 자리. 방이 따로 정하는 경우(온천 안이면 탕 출입구)는 landing으로 넘긴다. */
export type LandingFn = (bot: Capy) => THREE.Vector3 | null;

function landingSpot(bot: Capy, nav: NavGrid, landing?: LandingFn): THREE.Vector3 {
  const own = landing?.(bot);
  if (own) return own;
  for (let k = 0; k < 16; k++) {
    const ang = k / 16 * Math.PI * 2 + 0.3, x = bot.pos.x + Math.cos(ang) * 1.9, z = bot.pos.z + Math.sin(ang) * 1.9;
    if (!nav.blockedAt(x, z)) return new THREE.Vector3(x, 0, z);
  }
  return new THREE.Vector3(bot.pos.x + 1.9, 0, bot.pos.z);
}

/** T(가 속한 탑)를 향해 걸어가기 시작. 다가가면 climbStep이 뛰어오른다. */
export function startClimb(a: Capy, T: Capy): boolean {
  if (!canClimb(a, T)) return false;
  a.goal = null; a.napping = false; a.emote = null;
  a.mode = 'climb'; a.climbT = T; a.path = null; a.climbRetarget = 0;
  return true;
}

function hopOnto(a: Capy, T: Capy, t: number): boolean {
  if (!canClimb(a, T)) { a.mode = 'idle'; a.climbT = null; return false; }
  const top = stackOf(T).top;
  a.hop = { from: a.c.root.position.clone(), toBase: top, t0: t, dur: 0.75, arc: 1.3 };
  top.rider = a; a.mode = 'riding'; a.climbT = null; a.path = null; a.bathAmt = 0;
  return true;
}

/** 내려오기. 내 위에 탄 카피바라는 아래 카피바라 위로 한 칸 내려앉는다. then: 땅에 닿은 뒤 할 일. */
export function hopDown(a: Capy, t: number, nav: NavGrid, then?: (a: Capy) => void, landing?: LandingFn): void {
  const below = a.riding || a.hop?.toBase;
  if (!below) { then?.(a); return; }
  const above = a.rider;
  below.rider = above || null;
  if (above) { above.riding = null; above.hop = { from: above.c.root.position.clone(), toBase: below, t0: t, dur: 0.45, arc: 0.3 }; }
  a.riding = null; a.rider = null;
  const bot = stackOf(below).bottom;
  a.hop = { from: a.c.root.position.clone(), to: landingSpot(bot, nav, landing), t0: t, dur: 0.7, arc: 0.9 };
  a.mode = 'hopping'; a.afterHop = then || null;
}

/** 내 위에 탄 카피바라를 모두 털어낸다 (흔들흔들, 아기로 바뀔 때, 올라타기 허락을 끌 때). npcThen: NPC가 땅에 닿은 뒤 할 일. */
export function shakeOff(base: Capy, t: number, nav: NavGrid, npcThen?: (a: Capy) => void, landing?: LandingFn): void {
  let r = base.rider;
  base.rider = null;
  const bot = stackOf(base).bottom;
  while (r) {
    const next = r.rider;
    r.rider = null; r.riding = null;
    const to = landingSpot(bot, nav, landing);
    to.x += (Math.random() - 0.5) * 1.2; to.z += (Math.random() - 0.5) * 1.2;
    if (nav.blockedAt(to.x, to.z)) to.copy(landingSpot(bot, nav, landing));
    r.hop = { from: r.c.root.position.clone(), to, t0: t + Math.random() * 0.2, dur: 0.8, arc: 1.4 }; r.mode = 'hopping';
    r.afterHop = r.isPlayer ? null : (npcThen ?? null);
    r = next ?? null;
  }
}

/** 방마다 다른 걷기 규칙. ignore: 비켜 가지 않을 카피바라, speedK: 물속 속도, wet: 지금 자리의 물 깊이(0~1). */
export interface ClimbWalk { ignore?: (o: Capy) => boolean; speedK?: number; wet?: (x: number, z: number) => number }

/** step의 'climb' 부분: 탑 맨 아래로 걸어가 1.9m 안이면 뛰어오른다. 움직였으면 true.
 *  물에 잠기는 정도는 걸어간 프레임에만 맞춘다 (프로토타입은 바로 뛰어오르는 프레임에 일찍 끝난다). */
export function climbStep(a: Capy, t: number, dt: number, nav: NavGrid, others: readonly Capy[], w: ClimbWalk = {}): boolean {
  const T = a.climbT;
  if (!T || !canClimb(a, T)) { a.mode = 'idle'; a.climbT = null; return false; }
  const bot = stackOf(T).bottom, d = Math.hypot(bot.pos.x - a.pos.x, bot.pos.z - a.pos.z);
  if (d < 1.9) { hopOnto(a, T, t); return false; }
  if (!a.climbGoal) a.climbGoal = new THREE.Vector3();
  if (t > (a.climbRetarget || 0)) { a.climbGoal.set(bot.pos.x, 0, bot.pos.z); a.climbRetarget = t + 1.2; }
  const moving = moveTo(a, a.climbGoal, dt, t, nav, others, false, w.ignore, w.speedK ?? 1);
  if (!moving) { if (d < 4.6) hopOnto(a, T, t); else { a.mode = 'idle'; a.climbT = null; } }
  if (w.wet) a.bathAmt += (w.wet(a.pos.x, a.pos.z) - a.bathAmt) * Math.min(1, dt * 0.9);
  return moving;
}

// 뛰는 중이면 포물선으로, 타고 있으면 아래 카피바라 머리 위에 붙인다.
poseHooks.rider = (a, t) => {
  const c = a.c;
  if (a.hop) {
    const h = a.hop, p = Math.max(0, Math.min(1, (t - h.t0) / h.dur));
    const to = h.toBase ? mountPoint(h.toBase, vH) : vH.copy(h.to!);
    c.root.position.lerpVectors(h.from, to, p); c.root.position.y += Math.sin(p * Math.PI) * h.arc;
    if (h.toBase) a.targetFacing = h.toBase.facing + 0.15;
    if (p >= 1) {
      a.hop = null;
      if (h.toBase) { a.riding = h.toBase; h.toBase.rider = a; a.mode = 'riding'; }
      else { a.pos.set(h.to!.x, 0, h.to!.z); a.mode = 'idle'; const f = a.afterHop; a.afterHop = null; f?.(a); }
    }
    return;
  }
  const b = a.riding!;
  mountPoint(b, vH); c.root.position.copy(vH); a.pos.set(vH.x, 0, vH.z); a.targetFacing = b.facing + 0.15;
};
